import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Bot, User, Send, X, Minimize2, Maximize2, Loader2, MessageCircle, Headphones, Sparkles, Zap } from 'lucide-react';
import { toast } from 'sonner';

const AIChatbot = ({ isOpen, onClose, isMinimized, onToggleMinimize }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      loadChatHistory();
    }
  }, [isOpen, isMinimized]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history/${sessionId}`, {
        headers: getAuthHeader()
      });
      
      const history = response.data.map(record => ([
        { type: 'user', content: record.user_message, timestamp: record.timestamp },
        { type: 'ai', content: record.ai_response, timestamp: record.timestamp }
      ])).flat();
      
      setMessages(history);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat/send`,
        {
          message: inputMessage,
          session_id: sessionId
        },
        { headers: getAuthHeader() }
      );

      const aiMessage = {
        type: 'ai',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Chat error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={onToggleMinimize}
          className="bg-white hover:bg-slate-50 rounded-full w-20 h-20 shadow-2xl p-3 border-2 border-lime-600"
        >
          <img 
            src="/customer-assets/Ezy_Mobility_Logo_New.png" 
            alt="EZY" 
            className="w-full h-full object-contain"
          />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 h-[600px] shadow-2xl">
      <Card className="h-full flex flex-col border-2 border-lime-500 overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-lime-600 to-green-600 text-white p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img 
                src="/customer-assets/Ezy_Mobility_Logo_App.png" 
                alt="EZY" 
                className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-md"
              />
              <CardTitle className="text-lg">EZY Operations Assistant</CardTitle>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={onToggleMinimize}
                className="hover:bg-white/20 text-white p-1"
              >
                <Minimize2 className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                className="hover:bg-white/20 text-white p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-white/80 mt-1">
            Ask me anything about your fleet operations
          </p>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4" ref={scrollRef}>
            <div className="space-y-4 min-h-full">
              {messages.length === 0 && (
                <div className="text-center text-slate-500 mt-8">
                  <img 
                    src="/customer-assets/Ezy_Mobility_Logo_App.png" 
                    alt="EZY" 
                    className="w-16 h-16 mx-auto mb-3 object-contain"
                  />
                  <p className="text-sm">👋 Hello! I'm your EZY assistant.</p>
                  <p className="text-xs mt-2">Ask me about motorcycles, riders, maintenance, fines, or any fleet operations.</p>
                  <div className="mt-4 space-y-2 text-xs text-left">
                    <p className="font-semibold text-slate-700">Try asking:</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-600">
                      <li>"How many motorcycles need maintenance?"</li>
                      <li>"Show pending fines summary"</li>
                      <li>"Which bikes have insurance expiring soon?"</li>
                    </ul>
                  </div>
                </div>
              )}

              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.type === 'ai' && (
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 p-1">
                      <img 
                        src="/customer-assets/Ezy_Mobility_Logo_App.png" 
                        alt="EZY" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  
                  <div
                    className={`max-w-[75%] rounded-lg p-3 break-words ${
                      msg.type === 'user'
                        ? 'bg-gradient-to-r from-lime-600 to-green-600 text-white'
                        : 'bg-slate-100 text-slate-800'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    <p className={`text-xs mt-1 ${msg.type === 'user' ? 'text-white/70' : 'text-slate-500'}`}>
                      {new Date(msg.timestamp).toLocaleTimeString('en-AE', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>

                  {msg.type === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center p-1">
                    <img 
                      src="/customer-assets/Ezy_Mobility_Logo_New.png" 
                      alt="EZY" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-lime-600" />
                    <span className="text-xs text-slate-600">EZY is thinking...</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 border-t flex-shrink-0">
            <div className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="bg-gradient-to-r from-lime-600 to-green-600 hover:from-lime-700 hover:to-green-700"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2 text-center">
              Powered by EZY AI • Real-time ERP data
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIChatbot;
